import React, { useEffect, useState, useReducer, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Play, CheckCircle2, Terminal } from "lucide-react";

/**
 * HeroReel — Sprint D Addendum #1
 * Web-native promo reel that plays in the landing hero right column.
 * NOT an MP4 — Framer Motion + Tailwind. Loops ~24s across 6 scenes.
 *
 * Props:
 *   workers: [{ slug, name, role, avatar_url, character_traits: [...] }]
 *            The 6 functional workers from GET /api/workers/catalog.
 */
const SCENE_DURATIONS = [4000, 4000, 4000, 4000, 4000, 4000]; // ms — 24s loop

function useSceneClock(paused, onLoop) {
    const [scene, setScene] = useState(0);
    const timerRef = useRef(null);
    useEffect(() => {
        if (paused) return;
        timerRef.current = setTimeout(() => {
            setScene((s) => {
                const next = (s + 1) % SCENE_DURATIONS.length;
                if (next === 0) onLoop?.();
                return next;
            });
        }, SCENE_DURATIONS[scene]);
        return () => clearTimeout(timerRef.current);
    }, [scene, paused, onLoop]);
    return [scene, () => { clearTimeout(timerRef.current); setScene(0); }];
}

export default function HeroReel({ workers = [] }) {
    const prefersReducedMotion = useReducedMotion();
    const [paused, setPaused] = useState(false);
    // eslint-disable-next-line
    const [scene, resetScene] = useSceneClock(paused || prefersReducedMotion, () => {});

    // Pick the 6 functional workers (fallback to first 6 if not tagged)
    const six = workers.slice(0, 6);
    const ivan = workers.find((w) => w.slug === "ivan-legal") || workers[0];
    const sofia = workers.find((w) => w.slug === "sofia-data") || workers[1];

    if (workers.length < 2) {
        // Skeleton while catalog loads
        return (
            <div className="rounded-2xl border border-[#e8e8e3] bg-white shadow-2xl h-[440px] grid place-items-center">
                <div className="text-sm text-gray-400">Loading team…</div>
            </div>
        );
    }

    // Reduced motion: show the end frame statically with a "Watch the reel →" link.
    if (prefersReducedMotion) {
        return <EndFrameStatic onPlay={() => window.location.reload()} />;
    }

    return (
        <div
            data-testid="hero-reel"
            className="relative rounded-2xl border border-[#e8e8e3] bg-white shadow-[0_2px_4px_rgba(15,23,42,0.06),0_20px_60px_-20px_rgba(15,23,42,0.25)] overflow-hidden"
        >
            {/* Faux browser chrome */}
            <div className="px-3 py-2 border-b border-[#e8e8e3] bg-[#fafaf7] flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
                <span className="w-2 h-2 rounded-full bg-[#28c840]" />
                <div className="ml-3 text-[10px] text-gray-400 font-mono truncate">app.nexus-one.com/workers/ivan</div>
            </div>

            {/* Stage */}
            <div className="relative aspect-[4/3] bg-gradient-to-br from-[#fafaf7] via-white to-[#f0fdf4] overflow-hidden">
                <AnimatePresence mode="wait">
                    {scene === 0 && <Scene1 key="s1" six={six} />}
                    {scene === 1 && <Scene2 key="s2" worker={ivan} />}
                    {scene === 2 && <Scene3 key="s3" worker={ivan} />}
                    {scene === 3 && <Scene4 key="s4" worker={sofia} />}
                    {scene === 4 && <Scene5 key="s5" six={six} />}
                    {scene === 5 && <Scene6 key="s6" />}
                </AnimatePresence>
            </div>

            {/* Scene indicator + replay */}
            <div className="px-3 py-2 border-t border-[#e8e8e3] bg-white flex items-center justify-between">
                <div className="flex gap-1">
                    {SCENE_DURATIONS.map((_, i) => (
                        <span
                            key={i}
                            className={`w-6 h-0.5 rounded-full ${i === scene ? "bg-[#16a34a]" : "bg-[#e8e8e3]"}`}
                        />
                    ))}
                </div>
                <button
                    onClick={() => { setPaused(false); resetScene(); }}
                    data-testid="hero-reel-replay"
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-gray-900 uppercase tracking-widest"
                >
                    <Play className="w-3 h-3" strokeWidth={2} /> Replay
                </button>
            </div>
        </div>
    );
}

/* =============================== SCENES =============================== */

function resolveAv(url) {
    if (!url) return "https://images.unsplash.com/photo-1500648767791-00dcc994a43e";
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
}

function Portrait({ w, size = "w-16 h-16", ring = false }) {
    return (
        <img
            src={resolveAv(w.avatar_url)}
            alt={w.name}
            className={`${size} rounded-full object-cover border border-[#e8e8e3] ${ring ? "ring-2 ring-[#16a34a]/30" : ""}`}
        />
    );
}

/* Scene 1: 3x2 grid of the six workers fading in */
function Scene1({ six }) {
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 grid place-items-center p-6"
        >
            <div className="grid grid-cols-3 gap-6">
                {six.map((w, i) => (
                    <motion.div
                        key={w.slug}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.35, duration: 0.5 }}
                        className="flex flex-col items-center"
                    >
                        <Portrait w={w} size="w-14 h-14 sm:w-16 sm:h-16" />
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.35 + 0.25, duration: 0.4 }}
                            className="mt-2 text-center"
                        >
                            <div className="text-[10px] sm:text-[11px] font-semibold text-gray-900 leading-tight">{w.name}</div>
                            <div className="text-[9px] text-gray-500 uppercase tracking-widest leading-tight">{(w.role || "").split(" ")[0]}</div>
                        </motion.div>
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
}

/* Scene 2: spotlight Ivan + mini 3-column with typewriter */
function Scene2({ worker }) {
    const line = "I've reviewed the Acme MSA — 3 clauses need your attention…";
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 p-4 grid grid-cols-[80px_1fr_100px] gap-3"
        >
            {/* Left rail */}
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="wfos-card p-2 flex flex-col items-center gap-2">
                <Portrait w={worker} size="w-14 h-14" />
                <div className="text-[10px] font-semibold text-gray-900 text-center">{worker.name}</div>
                <div className="text-[9px] uppercase tracking-widest text-gray-500 text-center">Legal</div>
            </motion.div>
            {/* Center */}
            <div className="wfos-card p-3">
                <div className="text-[10px] text-gray-400 mb-1">{worker.name} · 09:24</div>
                <Typewriter text={line} speed={40} className="text-[12px] text-gray-900 leading-relaxed" />
            </div>
            {/* Right — task list ticking off */}
            <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="wfos-card p-2 space-y-1.5">
                {["Contract review", "Risk assessment", "Draft redlines"].map((t, i) => (
                    <motion.div
                        key={t}
                        initial={{ opacity: 0.4 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8 + i * 0.6 }}
                        className="flex items-center gap-1.5 text-[9.5px] text-gray-700"
                    >
                        <motion.span
                            initial={{ scale: 0.5 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.8 + i * 0.6 + 0.2 }}
                        >
                            <CheckCircle2 className="w-3 h-3 text-[#16a34a]" strokeWidth={2} />
                        </motion.span>
                        <span className="truncate">{t}</span>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
}

/* Scene 3: tool logos slide in around Ivan */
function Scene3({ worker }) {
    const tools = [
        { name: "Salesforce", angle: -140, label: "Living in your CRM" },
        { name: "Slack",      angle: -80,  label: "Living in your comms" },
        { name: "DocuSign",   angle: -20,  label: "Living in your contracts" },
        { name: "Notion",     angle: 40,   label: null },
        { name: "Zendesk",    angle: 100,  label: null },
    ];
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 grid place-items-center"
        >
            <div className="relative w-full max-w-md aspect-square">
                {/* Central worker */}
                <motion.div
                    initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                >
                    <div className="wfos-card p-3 flex items-center gap-2 min-w-[120px]">
                        <Portrait w={worker} size="w-10 h-10" />
                        <div>
                            <div className="text-[11px] font-semibold text-gray-900">{worker.name}</div>
                            <div className="text-[9px] text-gray-500">Legal</div>
                        </div>
                    </div>
                </motion.div>
                {/* Tool bubbles */}
                {tools.map((t, i) => {
                    const rad = (t.angle * Math.PI) / 180;
                    const x = Math.cos(rad) * 130;
                    const y = Math.sin(rad) * 130;
                    return (
                        <motion.div
                            key={t.name}
                            initial={{ scale: 0, x: 0, y: 0, opacity: 0 }}
                            animate={{ scale: 1, x, y, opacity: 1 }}
                            transition={{ delay: 0.5 + i * 0.2, duration: 0.5, type: "spring", stiffness: 120 }}
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        >
                            <div className="rounded-full bg-white border border-[#e8e8e3] shadow px-2 py-1 flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-[#16a34a]" />
                                <span className="text-[9.5px] font-medium text-gray-800 whitespace-nowrap">{t.name}</span>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
                className="absolute bottom-3 left-0 right-0 text-center text-[10px] text-gray-500 uppercase tracking-widest font-semibold"
            >
                Living in the tools you already use.
            </motion.div>
        </motion.div>
    );
}

/* Scene 4: Sofia w/ mini chart + KPI counter */
function Scene4({ worker }) {
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 grid grid-cols-2 gap-4 p-6 items-center"
        >
            <div>
                <Portrait w={worker} size="w-16 h-16" ring />
                <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Data Analyst</div>
                    <div className="text-lg font-semibold text-gray-900">{worker.name}</div>
                    <div className="text-[11px] text-gray-500 mt-1">Snowflake · Tableau · HubSpot</div>
                </div>
            </div>
            <div>
                <div className="wfos-card p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Revenue leakage detected</div>
                    <CountUp to={2100000} format="$" suffix="" className="text-3xl font-bold text-[#16a34a] tabular-nums" />
                    <MiniLineChart />
                </div>
            </div>
        </motion.div>
    );
}

/* Scene 5: 6 workers in a circle + task counter */
function Scene5({ six }) {
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 grid place-items-center"
        >
            <div className="relative w-full max-w-md aspect-square">
                <div className="absolute inset-0 grid place-items-center">
                    <div className="text-center">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Your Workforce</div>
                        <CountUp to={187} format="" suffix=" tasks today" className="text-3xl font-bold text-gray-900 tabular-nums" />
                    </div>
                </div>
                {six.slice(0, 6).map((w, i) => {
                    const rad = ((i / 6) * 2 * Math.PI) - Math.PI / 2;
                    const x = Math.cos(rad) * 140;
                    const y = Math.sin(rad) * 140;
                    return (
                        <motion.div
                            key={w.slug}
                            initial={{ scale: 0, x: 0, y: 0, opacity: 0 }}
                            animate={{ scale: 1, x, y, opacity: 1 }}
                            transition={{ delay: i * 0.15, type: "spring" }}
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        >
                            <Portrait w={w} size="w-12 h-12" ring />
                        </motion.div>
                    );
                })}
            </div>
        </motion.div>
    );
}

/* Scene 6: end frame with CTAs */
function Scene6() {
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[#f0fdf4] via-white to-[#ecfeff]"
        >
            <div className="text-center px-6">
                <div className="wfos-label text-gray-500">Nexus One</div>
                <motion.h2
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.5 }}
                    className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-gray-900"
                >
                    Your AI workforce is ready.
                </motion.h2>
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="mt-5 flex gap-2 justify-center"
                >
                    <motion.span
                        animate={{ scale: [1, 1.03, 1] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        className="rounded-lg bg-[#16a34a] px-4 h-9 text-[13px] font-semibold text-white grid place-items-center"
                    >
                        Start free
                    </motion.span>
                    <span className="rounded-lg border border-[#e8e8e3] bg-white px-4 h-9 text-[13px] font-medium text-gray-900 grid place-items-center">
                        Book a demo
                    </span>
                </motion.div>
            </div>
        </motion.div>
    );
}

function EndFrameStatic({ onPlay }) {
    return (
        <div className="rounded-2xl border border-[#e8e8e3] bg-gradient-to-br from-[#f0fdf4] via-white to-[#ecfeff] shadow-2xl h-[440px] grid place-items-center relative" data-testid="hero-reel-reduced-motion">
            <div className="text-center px-6">
                <div className="w-14 h-14 rounded-full bg-white grid place-items-center mx-auto shadow-md">
                    <Terminal className="w-6 h-6 text-[#16a34a]" strokeWidth={1.75} />
                </div>
                <div className="mt-4 text-2xl font-bold tracking-tight text-gray-900">Your AI workforce is ready.</div>
                <button
                    onClick={onPlay}
                    className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-[#16a34a] hover:text-[#15803d]"
                >
                    <Play className="w-3.5 h-3.5" strokeWidth={2} /> Watch the reel
                </button>
            </div>
        </div>
    );
}

/* ======== small internal helpers ======== */

function Typewriter({ text, speed = 30, className = "" }) {
    const [out, setOut] = useReducer((s, a) => a === "reset" ? "" : s + a, "");
    useEffect(() => {
        setOut("reset");
        let i = 0;
        const iv = setInterval(() => {
            i += 1;
            if (i > text.length) return clearInterval(iv);
            setOut(text[i - 1]);
        }, speed);
        return () => clearInterval(iv);
        // eslint-disable-next-line
    }, [text]);
    return <div className={className}>{out}<span className="wfos-caret" /></div>;
}

function CountUp({ to, className = "", format = "", suffix = "" }) {
    const [n, setN] = useState(0);
    useEffect(() => {
        const start = performance.now();
        const dur = 1500;
        let raf;
        const step = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.floor(eased * to));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [to]);
    return <div className={className}>{format}{n.toLocaleString()}{suffix}</div>;
}

function MiniLineChart() {
    const path = "M0 30 L 15 24 L 30 18 L 45 22 L 60 12 L 75 8 L 90 4";
    return (
        <svg viewBox="0 0 90 32" className="w-full mt-2" preserveAspectRatio="none">
            <motion.path
                d={path}
                fill="none"
                stroke="#16a34a"
                strokeWidth="1.5"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.2 }}
            />
        </svg>
    );
}
